import {
  CalendarDotsIcon,
  ChatsCircleIcon,
  FoldersIcon,
  GearSixIcon,
  HouseIcon,
  KeyIcon,
  ListChecksIcon,
  PlusIcon,
} from '@phosphor-icons/react';
import { FOLDER_COLORS } from '@regia/shared';
import { useState } from 'react';
import { NavLink, Outlet, ScrollRestoration } from 'react-router';
import { UploadIndicator } from '../features/media/UploadList';
import { useNotifications } from '../features/notifications';
import { usePages } from '../features/pages/api';
import { useMe } from '../lib/auth';
import { useUploads } from '../lib/uploads';
import { Avatar, BottomBar, Button, ColorDot, cn, type NavItem, Sidebar } from '../ui';
import { QuickActionsSheet } from './QuickActionsSheet';

function Wordmark() {
  return (
    <NavLink to="/" className="t-display text-[1.75rem] text-ink" aria-label="Regia, vai a Oggi">
      Regia
    </NavLink>
  );
}

function SidebarPages() {
  const pages = usePages();
  if (!pages.data?.length) return null;
  return (
    <div>
      <p className="px-3 pb-2 text-sm font-semibold text-ink-2">Le tue schede</p>
      <ul className="flex flex-col gap-0.5 pb-4">
        {pages.data.map((p) => (
          <li key={p.id}>
            <NavLink
              to={`/schede/${p.slug}`}
              className={({ isActive }) =>
                cn(
                  'flex h-10 items-center gap-3 rounded-xl px-3 text-[0.9375rem] transition-colors',
                  isActive
                    ? 'bg-ink/7 font-semibold text-ink'
                    : 'text-ink-2 hover:bg-ink/4 hover:text-ink',
                )
              }
            >
              <ColorDot color={FOLDER_COLORS[p.color].bg} size={10} />
              <span className="truncate">{p.name}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AppLayout() {
  const [plusOpen, setPlusOpen] = useState(false);
  const me = useMe();
  const unread = useNotifications().data?.unread ?? 0;
  const uploads = useUploads();

  const sideItems: NavItem[] = [
    { to: '/', label: 'Oggi', icon: HouseIcon, end: true },
    { to: '/chat', label: 'Chat', icon: ChatsCircleIcon },
    { to: '/schede', label: 'Schede', icon: FoldersIcon },
    { to: '/agenda', label: 'Agenda', icon: CalendarDotsIcon },
    { to: '/attivita', label: 'Attività', icon: ListChecksIcon },
    { to: '/accessi', label: 'Accessi', icon: KeyIcon },
  ];

  return (
    <div className="flex min-h-dvh">
      <Sidebar
        brand={
          <div className="flex items-center justify-between">
            <Wordmark />
            <Button
              size="sm"
              icon={PlusIcon}
              onClick={() => setPlusOpen(true)}
              aria-label="Carica o crea"
            >
              Nuovo
            </Button>
          </div>
        }
        items={sideItems}
        footer={
          <NavLink
            to="/impostazioni"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-xl p-2 transition-colors',
                isActive ? 'bg-ink/7' : 'hover:bg-ink/4',
              )
            }
          >
            <Avatar name={me?.name ?? ''} size={36} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-ink">{me?.name}</span>
              <span className="block truncate text-xs text-ink-3">Impostazioni</span>
            </span>
            <GearSixIcon size={18} className="text-ink-3" aria-hidden />
          </NavLink>
        }
      >
        <SidebarPages />
      </Sidebar>

      <main className="min-w-0 flex-1 pb-[calc(6rem+env(safe-area-inset-bottom))] safe-top lg:pb-16">
        <div className="mx-auto w-full max-w-[1240px]">
          <Outlet />
        </div>
      </main>

      <BottomBar
        items={[
          { to: '/', label: 'Oggi', icon: HouseIcon, end: true, badge: unread },
          { to: '/schede', label: 'Schede', icon: FoldersIcon },
          { to: '/agenda', label: 'Agenda', icon: CalendarDotsIcon },
          { to: '/chat', label: 'Chat', icon: ChatsCircleIcon },
        ]}
        onPlus={() => setPlusOpen(true)}
      />
      <UploadIndicator entries={uploads} />
      <QuickActionsSheet open={plusOpen} onOpenChange={setPlusOpen} />
      <ScrollRestoration />
    </div>
  );
}
